import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchDramaCategories, resolveMediaURL, uploadCreatorCover, type CreatorDraft, type DramaCategory } from '@/api/member';
import { ensureMemberAccessToken } from '@/utils/memberSession';
import { uploadCreatorEpisodeInChunks, type CreatorUploadStage } from '@/utils/creatorChunkUpload';

interface EpRow {
	id: string;
  num: number;
  title: string;
  duration: string;
  videoUrl: string;
	coverUrl: string;
  fileName: string;
  uploading: boolean;
  error: string;
	uploadProgress: number;
	uploadStage: CreatorUploadStage | '';
}

const creatorEpisodeMaxSize = 100 * 1024 * 1024;

export default function UploadPanel({
  onAdd,
}: {
  onAdd: (draft: CreatorDraft) => Promise<void>;
}) {
  const { t } = useTranslation('cinema');
  const fileInputRef = useRef<HTMLInputElement>(null);
	const rowSequenceRef = useRef(1);
	const uploadControllersRef = useRef(new Map<string, AbortController>());
  const [title, setTitle] = useState('');
  const [categories, setCategories] = useState<DramaCategory[]>([]);
  const [categoryId, setCategoryId] = useState(0);
  const [coverUrl, setCoverUrl] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverError, setCoverError] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [epRows, setEpRows] = useState<EpRow[]>([
    { id: 'episode-1', num: 1, title: '', duration: '', videoUrl: '', coverUrl: '', fileName: '', uploading: false, error: '', uploadProgress: 0, uploadStage: '' },
  ]);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    void fetchDramaCategories()
      .then((items) => {
        setCategories(items);
        setCategoryId((current) => current || items[0]?.id || 0);
      })
      .catch(() => setCategories([]));
  }, []);

	useEffect(() => () => {
		uploadControllersRef.current.forEach((controller) => controller.abort());
		uploadControllersRef.current.clear();
	}, []);

  const episodesReady = epRows.every((row) => row.title.trim().length > 0 && row.videoUrl.length > 0 && !row.uploading);
  const canSubmit = title.trim().length > 0 && categoryId > 0 && coverUrl.trim().length > 0 && !coverUploading && episodesReady;

  const handleCoverChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setCoverError(t('cinema_creatorCoverTypeError'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setCoverError(t('cinema_creatorCoverSizeError'));
      return;
    }
    setCoverUploading(true);
    setCoverError('');
    try {
      const token = await ensureMemberAccessToken();
      if (!token) throw new Error('unauthorized');
      const result = await uploadCreatorCover(file, token);
      setCoverUrl(result.url);
    } catch (cause) {
      setCoverError(cause instanceof Error ? cause.message : t('cinema_creatorCoverUploadFailed'));
    } finally {
      setCoverUploading(false);
    }
  };

  const addRow = () => {
		rowSequenceRef.current += 1;
		const id = `episode-${rowSequenceRef.current}`;
    setEpRows((prev) => [
      ...prev,
      { id, num: prev.length + 1, title: '', duration: '', videoUrl: '', coverUrl: '', fileName: '', uploading: false, error: '', uploadProgress: 0, uploadStage: '' },
    ]);
  };

  const removeRow = (idx: number) => {
		const rowId = epRows[idx]?.id;
		if (rowId) {
			uploadControllersRef.current.get(rowId)?.abort();
			uploadControllersRef.current.delete(rowId);
		}
    setEpRows((prev) =>
      prev.length <= 1
        ? prev
        : prev
            .filter((_, i) => i !== idx)
            .map((r, i) => ({ ...r, num: i + 1 }))
    );
  };

  const updateRow = (idx: number, patch: Partial<EpRow>) => {
    setEpRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

	const updateRowById = (id: string, patch: Partial<EpRow>) => {
		setEpRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
	};

  const handleEpisodeVideo = async (idx: number, file?: File) => {
    if (!file) return;
		const rowId = epRows[idx]?.id;
		if (!rowId) return;
    const lowerName = file.name.toLowerCase();
    const isSupportedName = lowerName.endsWith('.mp4') || lowerName.endsWith('.mov');
    const isSupportedMime = !file.type || file.type === 'video/mp4' || file.type === 'video/quicktime';
    if (!isSupportedName || !isSupportedMime) {
      updateRow(idx, { videoUrl: '', fileName: '', error: t('cinema_creatorEpisodeTypeError') });
      return;
    }
    if (file.size > creatorEpisodeMaxSize) {
      updateRow(idx, { videoUrl: '', fileName: file.name, error: t('cinema_creatorEpisodeSizeError') });
      return;
    }
		uploadControllersRef.current.get(rowId)?.abort();
		const controller = new AbortController();
		uploadControllersRef.current.set(rowId, controller);
		updateRowById(rowId, { uploading: true, error: '', fileName: file.name, videoUrl: '', uploadProgress: 0, uploadStage: 'hashing' });
    try {
			const result = await uploadCreatorEpisodeInChunks(file, {
				getToken: ensureMemberAccessToken,
				signal: controller.signal,
				onProgress: ({ stage, percent }) => updateRowById(rowId, { uploadStage: stage, uploadProgress: percent }),
			});
			updateRowById(rowId, { uploading: false, videoUrl: result.url, coverUrl: result.thumbnailUrl, fileName: file.name, uploadProgress: 100, uploadStage: '' });
    } catch (cause) {
			if (controller.signal.aborted) return;
			updateRowById(rowId, {
        uploading: false,
        videoUrl: '',
			uploadStage: '',
        error: cause instanceof Error ? cause.message : t('cinema_creatorEpisodeUploadFailed'),
      });
		} finally {
			if (uploadControllersRef.current.get(rowId) === controller) {
				uploadControllersRef.current.delete(rowId);
			}
    }
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await onAdd({
      title: title.trim(),
      categoryId,
      coverUrl: coverUrl.trim(),
      description: synopsis.trim(),
      episodes: epRows.map((r) => ({
        num: r.num,
        title: r.title.trim(),
        duration: r.duration.trim() || '12',
        videoUrl: r.videoUrl,
		coverUrl: r.coverUrl,
      })),
      });
    } catch (cause) {
      setSubmitError(cause instanceof Error ? cause.message : t('cinema_creatorSubmitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-background-900/50 border border-background-800/40 overflow-hidden">
        <div className="p-4 md:p-5">
          <label className="block text-sm font-medium text-foreground-200 mb-2">
            {t('cinema_creatorWorkName')} <span className="text-accent-400">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('cinema_creatorWorkNamePh')}
            maxLength={40}
            className="w-full h-11 px-4 rounded-xl bg-background-900/60 border border-background-800/80 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition-all"
          />
          {touched && !title.trim() && <p className="mt-2 text-xs text-rose-400">{t('cinema_creatorWorkNameRequired')}</p>}

          <label className="block text-sm font-medium text-foreground-200 mb-2 mt-5">
            {t('cinema_creatorCategory')}
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setCategoryId(item.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  categoryId === item.id
                    ? 'bg-accent-500 text-background-950'
                    : 'bg-foreground-50/6 text-foreground-400 hover:text-foreground-200'
                }`}
              >
                {item.name}
              </button>
            ))}
            {categories.length === 0 && <span className="text-xs text-foreground-600">{t('cinema_creatorCategoryEmpty')}</span>}
          </div>
          {touched && categoryId === 0 && <p className="mt-2 text-xs text-rose-400">{t('cinema_creatorCategoryRequired')}</p>}
        </div>
      </div>

      <div className="rounded-2xl bg-background-900/50 border border-background-800/40 p-4 md:p-5">
        <label className="block text-sm font-medium text-foreground-200 mb-1">
          {t('cinema_creatorCover')} <span className="text-accent-400">*</span>
        </label>
        <p className="text-xs text-foreground-500 mb-3">{t('cinema_creatorCoverHint')}</p>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(event) => void handleCoverChange(event)} />
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={coverUploading}
            className="relative flex h-40 w-28 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-dashed border-background-700 bg-background-900/60 text-foreground-500 transition-colors hover:border-accent-500/60 hover:text-accent-400 disabled:cursor-wait"
          >
            {coverUrl ? <img src={resolveMediaURL(coverUrl)} alt={t('cinema_creatorCover')} className="h-full w-full object-cover" /> : <span className="flex flex-col items-center gap-2"><i className={`${coverUploading ? 'ri-loader-4-line animate-spin' : 'ri-image-add-line'} text-2xl`} /><span className="text-xs">{coverUploading ? t('cinema_creatorCoverUploading') : t('cinema_creatorCoverUpload')}</span></span>}
          </button>
          <div className="min-w-0 space-y-2 text-xs text-foreground-500">
            <p>{t('cinema_creatorCoverFormat')}</p>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={coverUploading} className="inline-flex items-center gap-1.5 rounded-lg bg-accent-500 px-3 py-2 font-semibold text-background-950 hover:bg-accent-400 disabled:opacity-50"><i className="ri-upload-2-line" />{coverUrl ? t('cinema_creatorCoverReplace') : t('cinema_creatorCoverChoose')}</button>
            {coverUrl && <button type="button" onClick={() => setCoverUrl('')} className="ml-2 inline-flex items-center gap-1 text-rose-400"><i className="ri-delete-bin-line" />{t('cinema_creatorCoverRemove')}</button>}
          </div>
        </div>
        {coverError && <p className="mt-3 text-xs text-rose-400">{coverError}</p>}
        {touched && !coverUrl.trim() && !coverUploading && <p className="mt-3 text-xs text-rose-400">{t('cinema_creatorCoverRequired')}</p>}
      </div>

      <div className="rounded-2xl bg-background-900/50 border border-background-800/40 p-4 md:p-5">
        <label className="block text-sm font-medium text-foreground-200 mb-1">
          {t('cinema_creatorSynopsis')}
        </label>
        <textarea
          value={synopsis}
          onChange={(e) => setSynopsis(e.target.value.slice(0, 500))}
          placeholder={t('cinema_creatorSynopsisPh')}
          rows={4}
          maxLength={500}
          className="w-full px-4 py-3 rounded-xl bg-background-900/60 border border-background-800/80 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition-all resize-none"
        />
        <p className="text-right text-[11px] text-foreground-600 mt-1.5">{synopsis.length}/500</p>
      </div>

      <div className="rounded-2xl bg-background-900/50 border border-background-800/40 p-4 md:p-5">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-foreground-200">
            {t('cinema_creatorEpisodeTitle')}
          </label>
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-background-950 bg-accent-500 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t('cinema_creatorAddEpisode')}
          </button>
        </div>
        <p className="text-xs text-foreground-500 mb-3">{t('cinema_creatorEpisodeDesc')}</p>

        <div className="space-y-2">
          {epRows.map((row, idx) => (
            <div key={row.id} className="rounded-xl border border-background-800/70 bg-background-900/35 p-3">
              <div className="flex items-center gap-2">
                <span className="w-14 flex-shrink-0 flex items-center justify-center h-11 rounded-xl bg-background-800/50 text-xs font-bold text-foreground-400">
                  {t('cinema_creatorEpShort', { count: row.num })}
                </span>
                <input
                  type="text"
                  value={row.title}
                  onChange={(e) => updateRow(idx, { title: e.target.value })}
                  placeholder={t('cinema_creatorEpisodeTitlePh')}
                  className="flex-1 min-w-0 h-11 px-3 rounded-xl bg-background-900/60 border border-background-800/80 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 transition-all"
                />
                <input
                  type="number"
                  value={row.duration}
                  onChange={(e) => updateRow(idx, { duration: e.target.value })}
                  placeholder={t('cinema_creatorEpisodeDurPh')}
                  min={1}
                  className="w-20 h-11 px-2 rounded-xl bg-background-900/60 border border-background-800/80 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 transition-all text-center"
                />
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  disabled={epRows.length <= 1 || row.uploading}
                  aria-label={t('cinema_creatorCoverRemove')}
                  className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg text-foreground-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
              {touched && !row.title.trim() && <p className="mt-2 text-xs text-rose-400 sm:pl-16">{t('cinema_creatorEpisodeTitleRequired')}</p>}
              <div className="mt-2 flex min-w-0 items-center gap-2 pl-0 sm:pl-16">
                <label className={`inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors ${row.uploading ? 'cursor-wait bg-background-700 text-foreground-500' : 'bg-accent-500 text-background-950 hover:bg-accent-400'}`}>
                  <i className={row.uploading ? 'ri-loader-4-line animate-spin' : 'ri-video-upload-line'} />
                  {row.uploading
                    ? t('cinema_creatorEpisodeUploading')
                    : row.videoUrl
                      ? t('cinema_creatorEpisodeReplace')
                      : t('cinema_creatorEpisodeUpload')}
                  <input
                    type="file"
                    accept=".mp4,.mov,video/mp4,video/quicktime"
                    disabled={row.uploading}
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      void handleEpisodeVideo(idx, file);
                    }}
                  />
                </label>
                <span className={`min-w-0 truncate text-xs ${row.videoUrl ? 'text-emerald-400' : 'text-foreground-500'}`}>
                  {row.fileName || t('cinema_creatorEpisodeMp4Only')}
                </span>
				{row.uploading && <span className="ml-auto shrink-0 text-xs tabular-nums text-accent-400">{row.uploadProgress}%</span>}
              </div>
				{row.uploading && (
					<div className="mt-2 h-1 overflow-hidden rounded-full bg-background-800 sm:ml-16">
						<div className="h-full bg-accent-500 transition-[width] duration-200" style={{ width: `${row.uploadProgress}%` }} />
					</div>
				)}
              {touched && !row.videoUrl && !row.uploading && <p className="mt-2 text-xs text-rose-400 sm:pl-16">{t('cinema_creatorEpisodeVideoRequired')}</p>}
              {row.error && <p className="mt-2 text-xs text-rose-400 sm:pl-16">{row.error}</p>}
            </div>
          ))}
        </div>
      </div>

      {submitError && <p className="text-xs text-rose-400 px-1">{submitError}</p>}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full py-3.5 rounded-xl text-sm font-semibold text-background-950 bg-accent-500 hover:bg-accent-400 disabled:cursor-wait transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
      >
        <i className={submitting ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
        {submitting ? t('cinema_creatorSubmitting') : t('cinema_creatorSubmit')}
      </button>
    </div>
  );
}
